import { useCallback, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import { useApiCaller } from './ApiUtilities';
import { getSaveAnswersUrl } from './UrlUtilities';

// Server-side backstop for the model-test takers (IELTS / TOEFL / GRE) and any timed quiz.
// Persists in-progress answers to /submission/{id}/save-answers so an abandoned attempt — the
// browser is closed or crashes before the client can call /submission/end — can still be
// finalized and scored by the server's overdue sweep from its last saved answers.
//
// Two write paths:
//   scheduleSave(answers) — debounced, authenticated POST via the normal API caller, used while
//                           the tab is alive (fires ~debounceMs after the last answer change).
//   flush(answers?)       — immediate write that survives the tab closing: a keepalive fetch that
//                           carries the x-auth-jwt header (navigator.sendBeacon can't set it).
//                           Wired to pagehide + visibilitychange→hidden here, and callers invoke
//                           it on module transitions too.
//
// Both are best-effort: the authoritative grade still comes from the client /submission/end when
// it runs, and the server rejects a save on an already-finalized attempt (safe no-op).
export default function useAnswerAutosave(submissionId, { debounceMs = 2000 } = {}) {
    const CallApi = useApiCaller();
    const timerRef = useRef(null);
    // Latest serialized answers, updated synchronously on every scheduleSave call so the
    // pagehide/visibility flush always has the freshest snapshot even if the debounce hasn't fired.
    const latestRef = useRef(null);

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const scheduleSave = useCallback((answersArray) => {
        if (!submissionId) return;
        latestRef.current = answersArray;
        clearTimer();
        timerRef.current = setTimeout(() => {
            CallApi({
                url: getSaveAnswersUrl(submissionId),
                method: 'POST',
                payload: { answers: latestRef.current || [] },
                token: true,
                silent: true, // best-effort backstop — never surface a red toast for it
            }).catch(() => {});
        }, debounceMs);
    }, [submissionId, CallApi, debounceMs]);

    const flush = useCallback((answersArray) => {
        if (!submissionId) return;
        const arr = answersArray || latestRef.current;
        if (!arr) return;
        latestRef.current = arr;
        clearTimer();
        try {
            const jwt = Cookies.get('jwt_token');
            // keepalive lets this complete after the page starts unloading; the custom auth header
            // rules out navigator.sendBeacon (it can't set headers).
            fetch(getSaveAnswersUrl(submissionId), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(jwt ? { 'x-auth-jwt': jwt } : {}),
                },
                body: JSON.stringify({ answers: arr }),
                keepalive: true,
            }).catch(() => {});
        } catch { /* best-effort */ }
    }, [submissionId]);

    // Flush on tab hide / close so the server has the latest answers if the user never submits.
    useEffect(() => {
        if (!submissionId) return undefined;
        const onPageHide = () => flush();
        const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };
        window.addEventListener('pagehide', onPageHide);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('pagehide', onPageHide);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [submissionId, flush]);

    useEffect(() => () => clearTimer(), []);

    return { scheduleSave, flush };
}
